import { useDraggable } from "@dnd-kit/core";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, Tooltip, Typography } from "@mui/material";
import { MenuItemPreviewSearchFragment } from "apps/web/src/types";
import WarningIcon from "apps/web/src/components/universal/WarningIcon";
import { useMenuBuilderContext } from "../MenuBuilderProvider";
import { PANEL_MENU_ITEM_DRAG_TYPE, panelMenuItemDragId } from "./dndTypes";

interface MenuItemsPanelRowProps {
  menuItem: MenuItemPreviewSearchFragment;
}

export const MenuItemsPanelRow = ({ menuItem }: MenuItemsPanelRowProps) => {
  const { isEditable } = useMenuBuilderContext();

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: panelMenuItemDragId(menuItem.id),
    disabled: !isEditable,
    data: {
      type: PANEL_MENU_ITEM_DRAG_TYPE,
      menuItem,
    },
  });

  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        bgcolor: "common.white",
        border: theme => `1px solid ${theme.palette.greyscale[300]}`,
        borderRadius: "6px",
        px: 1,
        py: 1,
        width: "100%",
        opacity: isDragging ? 0.4 : 1,
        cursor: isEditable ? (isDragging ? "grabbing" : "grab") : "default",
        touchAction: "none",
      }}
    >
      <DragIndicatorIcon
        sx={{
          fontSize: 16,
          color: "greyscale.500",
          flexShrink: 0,
        }}
        aria-hidden
      />
      <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
        {menuItem.isMissingIngredients && (
          <WarningIcon sx={{ fontSize: "16px", mr: 0.5, flexShrink: 0 }} tip="Missing Ingredients" />
        )}
        <Tooltip title={menuItem.name} enterDelay={500}>
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ fontWeight: 500, lineHeight: "20px", minWidth: 0 }}
          >
            {menuItem.name}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
};
